import { SecureLoader } from "@ews-admin/global-design-system";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { setError } from "../../store/reducers/errorSlice";
import { selectSessionAuthState } from "../../store/reducers/sessionSlice";
import { errorCode, errorType } from "../../types/error.definition";
import type { AllowedRoles } from "../../types/session.definition";

/** Roles that may use the dashboard at all. */
const DASHBOARD_ROLES: AllowedRoles[] = ["manager", "secretary"];
import {
  getDashboardUrl,
  getLoginApplicationUrl,
} from "../../utils/config-utils";

interface PrivateRouteProps {
  children: React.ReactNode;
  allowedRoles: AllowedRoles[];
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sessionStatus, loading, session } = useSelector(
    selectSessionAuthState
  );
  const role = session?.user?.roleName?.toLowerCase() as AllowedRoles;
  // MED-1687: a signed-in secretary typing a manager URL (/manager/staff...) was sent to the
  // login page, as if signed out. She now stays signed in, back on the dashboard, with a message.
  const isDashboardUserWithoutAccess =
    sessionStatus === "VALID_SESSION" && DASHBOARD_ROLES.includes(role) && !allowedRoles.includes(role);

  useEffect(() => {
    if (isDashboardUserWithoutAccess) {
      dispatch(setError({ code: errorCode.FORBIDDEN_ACTION, category: errorType.WARNING_ERROR }));
    }
  }, [isDashboardUserWithoutAccess, dispatch]);
  const secureLoader = (
    <SecureLoader
      label={t("common.loading")}
      description={t("common.secureLoading")}
    />
  );

  // Show loading while session is being validated
  if (loading || sessionStatus === null) {
    return secureLoader;
  }

  // Redirect to external auth if session is invalid
  if (sessionStatus !== "VALID_SESSION") {
    const redirectTo = getDashboardUrl();
    const language = localStorage.getItem("language") || "fr";
    const loginUrl = new URL(getLoginApplicationUrl(redirectTo));
    loginUrl.searchParams.set("language", language);
    window.location.href = loginUrl.toString();
    return secureLoader; // Show loader while redirecting
  }

  if (isDashboardUserWithoutAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  // Any other role (patient, doctor...) has nothing to do here: back to the login app
  if (!allowedRoles.includes(role)) {
    const redirectTo = getDashboardUrl();
    const language = localStorage.getItem("language") || "fr";
    const loginUrl = new URL(getLoginApplicationUrl(redirectTo));
    loginUrl.searchParams.set("language", language);
    window.location.href = loginUrl.toString();
    return secureLoader; // Show loader while redirecting
  }

  return <>{children}</>;
};
